<template>
  <div class="pd-both">
    <div class="matching-page-title">
      <router-link
        :to="`/recruiting/${opportunityId}`"
        class="matching-page-back"
      >
        &larr; Back to opportunity
      </router-link>
    </div>

    <md-card class="mt-20">
      <md-card-header md-alignment="space-between" class="md-title matching-header">
        <div>
          Matching
          <span v-if="matchingState === 'ready'" class="matching-count">
            ({{ matches.length }} candidates)
          </span>
        </div>
        <div v-if="matchingState !== 'loading'" class="matching-header-actions">
          <md-button
            class="md-raised md-accent md-round capitalize md-white-color"
            :disabled="matchingRunning"
            @click="runMatching"
          >
            {{ matchingState === 'empty' ? 'Run matching' : 'Re-run' }}
          </md-button>
          <!-- Loosen-filter chips: toggle to drop specific constraints before
               Re-Run. Handy when an opportunity's location/skills/industry are
               so narrow that vectordb returns zero matches. -->
          <div class="loosen-chips">
            <button
              type="button"
              class="loosen-chip"
              :class="{ 'loosen-chip--active': loosenFilters.ignoreLocation }"
              @click="toggleLoosen('ignoreLocation')"
              :title="'Send location:[] + remote:true — bypass the strict location filter'"
            >
              <span v-if="loosenFilters.ignoreLocation">✕ </span>Ignore location
            </button>
            <button
              v-for="skill in opportunitySkills"
              :key="`skill-${skill}`"
              type="button"
              class="loosen-chip"
              :class="{ 'loosen-chip--active': isSkillExcluded(skill) }"
              @click="toggleExcludedSkill(skill)"
              :title="`Exclude '${skill}' from the skills filter`"
            >
              <span v-if="isSkillExcluded(skill)">✕ </span>{{ skill }}
            </button>
            <button
              v-for="ind in opportunityIndustries"
              :key="`industry-${ind}`"
              type="button"
              class="loosen-chip"
              :class="{ 'loosen-chip--active': isIndustryExcluded(ind) }"
              @click="toggleExcludedIndustry(ind)"
              :title="`Exclude '${ind}' from the industry filter`"
            >
              <span v-if="isIndustryExcluded(ind)">✕ </span>{{ ind }}
            </button>
            <button
              type="button"
              class="loosen-chip"
              :class="{ 'loosen-chip--active': loosenFilters.includeEmployer }"
              @click="toggleLoosen('includeEmployer')"
              :title="'Allow candidates who already work at the employer'"
            >
              <span v-if="loosenFilters.includeEmployer">✕ </span>Include current employer
            </button>
          </div>
          <!-- Multiselect bar sits inline right next to Re-Run so the whole
               row-level toolbar reads as one action strip. -->
          <div v-if="selectedIds.length > 0" class="matching-bulk-bar">
            <span class="matching-bulk-bar__count">
              <strong>{{ selectedIds.length }}</strong> selected
            </span>
            <md-button
              class="md-raised md-accent md-round capitalize md-white-color"
              :disabled="pendingSelectedCount === 0"
              @click="approveSelected"
            >
              Approve selected ({{ pendingSelectedCount }})
            </md-button>
            <md-button
              class="md-raised md-accent md-round capitalize md-white-color m-left-10 md-orange"
              :disabled="pendingSelectedCount === 0"
              @click="rejectSelected"
            >
              Reject selected ({{ pendingSelectedCount }})
            </md-button>
            <md-button
              class="md-raised md-round capitalize m-left-10"
              @click="clearSelection"
            >
              Clear
            </md-button>
          </div>
        </div>
      </md-card-header>
      <md-card-content>
        <md-empty-state
          v-if="matchingState === 'empty'"
          md-label="No matches generated yet"
          md-description="Use the toolbar above — toggle any filter chips you want to loosen, then hit Run matching.">
        </md-empty-state>

        <div
          v-else-if="matchingState === 'loading'"
          class="matching-loader"
          role="status"
          aria-live="polite"
        >
          Finding matches<span
            class="matching-loader__dots"
            aria-hidden="true"
          ><span>.</span><span>.</span><span>.</span></span>
        </div>

        <template v-else>
          <!-- Ran successfully but Vector DB returned zero candidates. Copy
               adapts to whether the user still has filters to loosen or has
               already dropped all of them. -->
          <md-empty-state
            v-if="matches.length === 0"
            md-icon="search_off"
            md-label="No candidates matched"
            :md-description="allChipsActive
              ? 'Even with every filter loosened, Vector DB found no matching alumni. The opportunity may be too narrow for this community, or there is not enough indexed data yet.'
              : (anyChipActive
                ? 'No candidates matched with the current filters. Try excluding more chips above (or all of them) and hit Re-run.'
                : 'No candidates matched. Try excluding a specific skill or industry chip above — or loosen Ignore location / Include current employer — and hit Re-run.')">
          </md-empty-state>

          <md-table v-else class="matching-table">
            <md-table-row>
              <md-table-head class="card-header matching-select-col">
                <md-checkbox
                  v-model="allPendingSelectedProxy"
                  :disabled="pendingCount === 0"
                />
              </md-table-head>
              <md-table-head class="card-header">Candidate</md-table-head>
              <md-table-head class="card-header">Score</md-table-head>
              <md-table-head class="card-header">Match reasons</md-table-head>
              <md-table-head class="card-header">Status</md-table-head>
              <md-table-head class="card-header matching-actions-col">Actions</md-table-head>
            </md-table-row>
            <md-table-row v-for="m in matches" :key="m.advisor_id">
              <md-table-cell class="matching-select-col">
                <md-checkbox
                  v-model="selectedIds"
                  :value="m.advisor_id"
                  :disabled="m.status !== 'pending'"
                />
              </md-table-cell>
              <md-table-cell>
                <router-link
                  :to="`/members/${m.advisor_id}`"
                  class="match-name-link"
                >{{ m.name }}</router-link>
                <div class="match-sub">
                  {{ ((m.dimensions && m.dimensions.location) || []).slice(0, 1).join('') }}
                  <span v-if="m.dimensions && m.dimensions.education && m.dimensions.education.length">
                    · {{ m.dimensions.education[0] }}
                  </span>
                </div>
              </md-table-cell>
              <md-table-cell>{{ m.score + '%' }}</md-table-cell>
              <md-table-cell>
                <span
                  v-for="tag in matchTags(m)"
                  :key="tag"
                  class="skill-chip match-chip"
                >{{ tag }}</span>
              </md-table-cell>
              <md-table-cell>
                <span :class="statusColorClass(m.status)">
                  {{ statusMatchLabel(m.status) }}
                </span>
              </md-table-cell>
              <md-table-cell class="matching-actions-col">
                <template v-if="m.status === 'pending'">
                  <md-button
                    class="md-raised md-accent md-round capitalize md-white-color m-left-10"
                    @click="approveMatch(m.advisor_id)"
                  >
                    Approve
                  </md-button>
                  <md-button
                    class="md-raised md-accent md-round capitalize md-white-color m-left-10 md-orange"
                    @click="rejectMatch(m.advisor_id)"
                  >
                    Reject
                  </md-button>
                </template>
                <md-button
                  v-else
                  class="md-raised md-accent md-round capitalize md-white-color m-left-10"
                  @click="resetMatch(m.advisor_id)"
                >
                  Undo
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>
        </template>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import axios from 'axios'
import { CORE_API_URL } from '../services/opportunityAdapter'
import {
  getRunState,
  saveRunState,
  setMatchStatus,
} from '../services/recruitingMatchesStore'

export default {
  name: 'RecruitingMatch',
  data() {
    return {
      matchingState: 'empty',
      matches: [],
      matchingRunning: false,
      selectedIds: [],
      opportunity: null,
      excludedSkills: [],
      excludedIndustries: [],
      // Filter overrides sent alongside the Re-Run request
      loosenFilters: {
        ignoreLocation: false,
        includeEmployer: false,
      },
    }
  },
  computed: {
    opportunityId() {
      return this.$route.params.opportunity_id
    },
    opportunitySkills() {
      return (this.opportunity && this.opportunity.skills) || []
    },
    opportunityIndustries() {
      return (this.opportunity && this.opportunity.industry) || []
    },
    // "Some chip is active" — includes fixed chips + per-skill/industry exclusions.
    anyChipActive() {
      return (
        Object.values(this.loosenFilters).some(Boolean) ||
        this.excludedSkills.length > 0 ||
        this.excludedIndustries.length > 0
      )
    },
    // "Every chip is active" — drives the "even loosened, no matches" copy.
    allChipsActive() {
      const allLoosen = Object.values(this.loosenFilters).every(Boolean)
      const allSkills =
        this.opportunitySkills.length > 0 &&
        this.opportunitySkills.every((s) => this.excludedSkills.indexOf(s) !== -1)
      const allInds =
        this.opportunityIndustries.length > 0 &&
        this.opportunityIndustries.every((i) => this.excludedIndustries.indexOf(i) !== -1)
      return allLoosen && allSkills && allInds
    },
    pendingCount() {
      return this.matches.filter((m) => m.status === 'pending').length
    },
    pendingSelectedCount() {
      return this.matches
        .filter((m) => this.selectedIds.indexOf(m.advisor_id) !== -1)
        .filter((m) => m.status === 'pending')
        .length
    },
    allPendingSelected() {
      const pending = this.matches.filter((m) => m.status === 'pending')
      if (!pending.length) return false
      return pending.every(
        (m) => this.selectedIds.indexOf(m.advisor_id) !== -1,
      )
    },
    // v-model proxy for the header select-all checkbox.
    allPendingSelectedProxy: {
      get() {
        return this.allPendingSelected
      },
      set(newVal) {
        const pendingIds = this.matches
          .filter((m) => m.status === 'pending')
          .map((m) => m.advisor_id)
        if (newVal) {
          const missing = pendingIds.filter(
            (id) => this.selectedIds.indexOf(id) === -1,
          )
          this.selectedIds = this.selectedIds.concat(missing)
        } else {
          this.selectedIds = this.selectedIds.filter(
            (id) => pendingIds.indexOf(id) === -1,
          )
        }
      },
    },
  },
  created() {
    // DB hydrate override — the DB is the source of truth across sessions/devices.
    const prior = getRunState(this.opportunityId)
    if (prior && Array.isArray(prior.matches) && prior.matches.length) {
      this.matches = prior.matches
      this.matchingState = 'ready'
    }
    this.hydrateFromDb()
    this.loadOpportunity()
  },
  methods: {
    matchTags(m) {
      const skills = ((m && m.dimensions && m.dimensions.skills) || []).slice(0, 3)
      const industries = ((m && m.industry) || []).slice(0, 1)
      return skills.concat(industries)
    },
    statusMatchLabel(status) {
      const labels = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', sent: 'Sent' }
      return labels[status] || status
    },
    statusColorClass(status) {
      if (status === 'approved') return 'md-green-color'
      if (status === 'sent') return 'md-blue-color'
      if (status === 'rejected') return 'md-red-color'
      return 'md-orange-color'
    },
    persist() {
      saveRunState(this.opportunityId, {
        ranAt: new Date().toISOString(),
        matches: this.matches,
      })
    },
    toggleLoosen(key) {
      this.$set(this.loosenFilters, key, !this.loosenFilters[key])
    },
    async loadOpportunity() {
      try {
        const token = localStorage.getItem('auth0_token')
        const { data } = await axios.get(
          `${CORE_API_URL}/api/opportunities/${this.opportunityId}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        )
        this.opportunity = data
      } catch (e) {
        // Non-fatal — chip row simply stays without dynamic chips.
        this.opportunity = null
      }
    },
    // Hydrate matches from the DB — source of truth across sessions/devices.
    // Overrides the localStorage cache when the DB has records for this opp.
    async hydrateFromDb() {
      try {
        const token = localStorage.getItem('auth0_token')
        const { data } = await axios.get(
          `${CORE_API_URL}/api/opportunity-matches/by-opportunity/${this.opportunityId}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        )
        const rows = Array.isArray(data) ? data : []
        if (!rows.length) return
        this.matches = rows.map((m) => this.dbMatchToLocal(m))
        this.matchingState = 'ready'
        this.persist()
      } catch (e) {
        // Non-fatal — cache stays as the fallback view.
      }
    },
    // Shape a persisted match doc into the row format the template renders.
    dbMatchToLocal(m) {
      const populated = m.member && typeof m.member === 'object'
      const memberId = populated ? m.member._id : m.member
      const first = populated ? (m.member.first || '') : ''
      const last = populated ? (m.member.last || '') : ''
      const displayName = populated ? (m.member.displayName || '') : ''
      const name =
        [first, last].filter(Boolean).join(' ').trim() ||
        displayName ||
        `Member ${String(memberId || '').slice(-6)}`
      return {
        _id: m._id,
        match_id: m._id,
        advisor_id: memberId,
        name,
        email: (populated && Array.isArray(m.member.emails) && m.member.emails[0]) || '',
        score: Math.round((m.similarity || 0) * 100),
        match_facets: m.matchFacets || [],
        dimensions: {
          location: [],
          education: [],
          skills:
            populated && m.member.dimensions && Array.isArray(m.member.dimensions.skills)
              ? m.member.dimensions.skills
              : [],
        },
        industry: populated && Array.isArray(m.member.industry) ? m.member.industry : [],
        status: this.localStatusFromDb(m.status, m),
      }
    },
    // DB pending_send/approved/rejected/sent/... → UI pending/approved/rejected/sent.
    // Interim: pending_send + mercuryId → sent (Mercury dispatched, webhook pending).
    localStatusFromDb(dbStatus, match) {
      if (dbStatus === 'pending_send' && match && match.email && match.email.mercuryId) return 'sent'
      if (dbStatus === 'pending_send') return 'pending'
      return dbStatus || 'pending'
    },
    isSkillExcluded(skill) {
      return this.excludedSkills.indexOf(skill) !== -1
    },
    isIndustryExcluded(industry) {
      return this.excludedIndustries.indexOf(industry) !== -1
    },
    toggleExcludedSkill(skill) {
      const i = this.excludedSkills.indexOf(skill)
      if (i === -1) this.excludedSkills.push(skill)
      else this.excludedSkills.splice(i, 1)
    },
    toggleExcludedIndustry(industry) {
      const i = this.excludedIndustries.indexOf(industry)
      if (i === -1) this.excludedIndustries.push(industry)
      else this.excludedIndustries.splice(i, 1)
    },
    // Build the filter-override payload from the current chip state.
    buildFilterOverrides() {
      const f = {}
      if (this.loosenFilters.ignoreLocation) {
        f.location = []
        f.remote = true
      }
      if (this.opportunitySkills.length > 0) {
        f.skills = this.opportunitySkills.filter(
          (s) => !this.isSkillExcluded(s)
        )
      }
      if (this.opportunityIndustries.length > 0) {
        f.industry = this.opportunityIndustries.filter(
          (i) => !this.isIndustryExcluded(i)
        )
      }
      if (this.loosenFilters.includeEmployer) f.exclude_employer = false
      return Object.keys(f).length ? f : null
    },
    async runMatching() {
      this.matchingRunning = true
      this.matchingState = 'loading'
      try {
        const token = localStorage.getItem('auth0_token')
        const authHeader = token ? { Authorization: `Bearer ${token}` } : {}
        const overrides = this.buildFilterOverrides()
        const body = { opportunityId: this.opportunityId }
        if (overrides) body.filters = overrides
        // POST /api/matching/match returns IOpportunityMatch[].
        const { data } = await axios.post(
          `${CORE_API_URL}/api/matching/match`,
          body,
          { headers: authHeader },
        )
        const rawMatches = Array.isArray(data) ? data : []

        // Hydrate each match with member details via /api/members/:id in parallel.
        const memberFetches = await Promise.all(
          rawMatches.map((m) =>
            axios
              .get(`${CORE_API_URL}/api/members/${m.member}`, { headers: authHeader })
              .then((r) => r.data)
              .catch(() => null),
          ),
        )

        // Preserve prior approvals/rejections keyed by member id.
        const priorByAdvisor = this.matches.reduce((acc, m) => {
          acc[m.advisor_id] = m.status
          return acc
        }, {})

        this.matches = rawMatches.map((m, idx) => {
          const member = memberFetches[idx] || {}
          const displayName =
            member.name ||
            [member.first, member.last].filter(Boolean).join(' ') ||
            `Member ${String(m.member || '').slice(-6)}`
          return {
            // Core-api identifiers — needed for the /review call.
            _id: m._id,
            match_id: m._id,
            advisor_id: m.member,
            // UI display fields.
            name: displayName,
            email: (member.emails && member.emails[0]) || '',
            score: Math.round((m.similarity || 0) * 100),
            match_facets: m.matchFacets || [],
            dimensions: {
              location: member.location ? [member.location] : [],
              education: member.education || [],
              skills: member.inferredSkills || member.skills || [],
            },
            industry: member.inferredIndustries || [],
            status: priorByAdvisor[m.member] || 'pending',
          }
        })
        this.matchingState = 'ready'
      } catch (err) {
        console.error('[recruiting/matching] runMatching failed:', err)
        this.matches = []
        this.matchingState = 'empty'
        if (this.$toast) {
          this.$toast.error('Matching engine failed. Please try again.')
        }
      } finally {
        this.matchingRunning = false
        this.persist()
      }
    },
    // Fire-and-forget PATCH — DB is the sync channel with dashboard; localStorage stays authoritative for hermes UX.
    persistMatchStatus(matchId, dbStatus) {
      if (!matchId) return
      const token = localStorage.getItem('auth0_token')
      axios
        .patch(
          `${CORE_API_URL}/api/opportunity-matches/${matchId}/status`,
          { status: dbStatus },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        )
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[recruiting/matching] persistMatchStatus failed', err && err.message)
        })
    },
    approveMatch(advisorId) {
      const m = this.matches.find((x) => x.advisor_id === advisorId)
      if (m) {
        m.status = 'approved'
        setMatchStatus(this.opportunityId, advisorId, 'approved')
        this.persistMatchStatus(m._id || m.match_id, 'approved')
      }
    },
    rejectMatch(advisorId) {
      const m = this.matches.find((x) => x.advisor_id === advisorId)
      if (m) {
        m.status = 'rejected'
        setMatchStatus(this.opportunityId, advisorId, 'rejected')
        this.persistMatchStatus(m._id || m.match_id, 'rejected')
      }
    },
    resetMatch(advisorId) {
      const m = this.matches.find((x) => x.advisor_id === advisorId)
      if (m) {
        m.status = 'pending'
        setMatchStatus(this.opportunityId, advisorId, 'pending')
        // Undo reverts to the real 'pending' review state — NOT 'pending_send' (which
        // means queued-to-send), so it no longer logs a phantom send. SCRUM-687.
        this.persistMatchStatus(m._id || m.match_id, 'pending')
      }
    },
    // ─── Multiselect helpers ──
    clearSelection() {
      this.selectedIds = []
    },
    approveSelected() {
      this.selectedIds.forEach((id) => {
        const m = this.matches.find((x) => x.advisor_id === id)
        if (m && m.status === 'pending') {
          m.status = 'approved'
          setMatchStatus(this.opportunityId, id, 'approved')
          this.persistMatchStatus(m._id || m.match_id, 'approved')
        }
      })
      this.clearSelection()
    },
    rejectSelected() {
      this.selectedIds.forEach((id) => {
        const m = this.matches.find((x) => x.advisor_id === id)
        if (m && m.status === 'pending') {
          m.status = 'rejected'
          setMatchStatus(this.opportunityId, id, 'rejected')
          this.persistMatchStatus(m._id || m.match_id, 'rejected')
        }
      })
      this.clearSelection()
    },
  },
}
</script>

<style scoped>
.matching-page-title { padding-top: 12px; padding-bottom: 12px; margin-left: 16px; margin-right: 16px; }
.matching-page-back { font-size: 18px; color: #6b7280; text-decoration: none; }
.matching-page-back:hover { color: #111827; text-decoration: underline; }
.matching-header { padding-bottom: 12px; }
.matching-header-actions { padding-top: 16px; }
.matching-count { color: #6c757d; font-weight: 400; margin-left: 4px; }
.match-name-link { font-weight: 600; color: #111827; text-decoration: none; }
.match-name-link:hover { text-decoration: underline; }
.match-sub { font-size: 12px; color: #6c757d; margin-top: 2px; }
.skill-chip { background: #eef2ff; color: #3730a3; padding: 4px 10px; border-radius: 10px; font-size: 12px; }
.match-chip { display: inline-block; margin: 2px 4px 2px 0; }
.matching-loader {
  text-align: center;
  padding: 48px 20px;
  font-family: 'Roboto Slab', Georgia, serif;
  font-size: 18px;
  color: #6c757d;
}
.matching-loader__dots { display: inline-block; margin-left: 2px; }
.matching-loader__dots span {
  display: inline-block;
  animation: matching-dot 1.2s infinite ease-in-out;
}
.matching-actions-col { text-align: left; }
.matching-select-col { width: 56px; padding-left: 16px !important; }
.matching-select-col >>> .md-checkbox { margin: 0; }
.matching-bulk-bar {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
  padding: 6px 10px;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
}
.matching-bulk-bar__count { font-size: 14px; color: #3730a3; margin-right: 8px; }
.matching-header-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0;
}
.matching-loader__dots span:nth-child(2) { animation-delay: 0.15s; }
.matching-loader__dots span:nth-child(3) { animation-delay: 0.3s; }
@keyframes matching-dot {
  0%, 60%, 100% { opacity: 0.2; transform: translateY(0); }
  30%           { opacity: 1;   transform: translateY(-3px); }
}

.loosen-chips {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: 12px;
}
.loosen-chip {
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  font: inherit;
  font-size: 12px;
  line-height: 1;
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.loosen-chip:hover {
  border-color: #dc2626;
  color: #b91c1c;
}
.loosen-chip--active {
  background: #fef2f2;
  border-color: #dc2626;
  color: #b91c1c;
  font-weight: 600;
}
.loosen-chip--active > span:first-child {
  margin-right: 6px;
}
</style>



// WEBPACK FOOTER //
// src/components/RecruitingMatch.vue